import React, { useState, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import api from "../api";
import ImageEditorModal from "./ImageEditorModal";

/* ─── Toolbar Button ─── */
function TBtn({ onClick, title, active, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className={`w-8 h-8 flex items-center justify-center rounded-md text-sm transition-colors
        ${active ? "bg-[#0071e3] text-white" : "text-[#1d1d1f] hover:bg-[#f5f5f7]"}`}
    >
      {children}
    </button>
  );
}

/* ─── Image Upload Zone ─── */
export function ImageUploadZone({ onUploaded, compact, uploadEndpoint }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await api.post(uploadEndpoint || "/api/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded(res.data.url);
    } catch (err) {
      alert("이미지 업로드 실패: " + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) upload(file);
  };

  const handlePaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of items) {
      if (item.type.startsWith("image/")) {
        upload(item.getAsFile());
        break;
      }
    }
  };

  if (compact) {
    return (
      <>
        <input ref={inputRef} type="file" accept="image/*" className="hidden"
          onChange={(e) => upload(e.target.files?.[0])} />
        <TBtn onClick={() => inputRef.current?.click()} title="이미지 업로드">
          {uploading ? <span className="animate-spin">⏳</span> : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
            </svg>
          )}
        </TBtn>
      </>
    );
  }

  return (
    <div
      onDrop={handleDrop}
      onDragOver={(e) => e.preventDefault()}
      onPaste={handlePaste}
      className="border-2 border-dashed border-[#d2d2d7] rounded-xl p-6 text-center hover:border-[#0071e3] transition cursor-pointer"
      onClick={() => inputRef.current?.click()}
    >
      <input ref={inputRef} type="file" accept="image/*" multiple className="hidden"
        onChange={async (e) => { for (const f of e.target.files) await upload(f); }} />
      {uploading ? (
        <p className="text-[#0071e3] text-sm">업로드 중...</p>
      ) : (
        <>
          <svg className="w-8 h-8 mx-auto text-[#86868b] mb-2" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5" />
          </svg>
          <p className="text-sm text-[#86868b]">클릭 또는 드래그하여 이미지 업로드</p>
          <p className="text-xs text-[#d2d2d7] mt-1">PNG, JPG, GIF, WebP 지원 · 최대 10MB</p>
        </>
      )}
    </div>
  );
}

/* ─── Main Image Uploader (메인 이미지) ─── */
function MainImageUploader({ imageUrl, onChange }) {
  const [editing, setEditing] = useState(false);
  return (
    <div className="space-y-2">
      <div className="flex gap-3 items-end">
        <div className="flex-1">
          <input
            value={imageUrl || ""}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-[#d2d2d7] text-sm focus:border-[#0071e3] focus:ring-1 focus:ring-[#0071e3] outline-none"
            placeholder="이미지 URL 또는 업로드"
          />
        </div>
        <ImageUploadZone compact onUploaded={(url) => onChange(url)} />
      </div>
      {imageUrl && (
        <div className="relative group w-20 h-20 rounded-lg overflow-hidden border border-[#d2d2d7] bg-[#fafafa]">
          <img src={imageUrl} alt="미리보기" className="w-full h-full object-contain" onError={(e) => { e.target.style.display = "none"; }} />
          <button onClick={() => setEditing(true)}
            className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center text-white text-xs font-semibold">
            ✏️ 편집
          </button>
        </div>
      )}
      {editing && imageUrl && (
        <ImageEditorModal imageUrl={imageUrl} onSave={(url) => { onChange(url); setEditing(false); }} onClose={() => setEditing(false)} />
      )}
    </div>
  );
}

/* ─── Detail Images Manager (DnD 지원) ─── */
function DetailImagesManager({ images, onChange }) {
  const [editingIdx, setEditingIdx] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  const removeImage = (idx) => onChange(images.filter((_, i) => i !== idx));
  const moveImage = (idx, dir) => {
    const arr = [...images];
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= arr.length) return;
    [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
    onChange(arr);
  };

  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; };
  const handleDragOver = (e, idx) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDragOverIdx(idx); };
  const handleDrop = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
    const arr = [...images];
    const [moved] = arr.splice(dragIdx, 1);
    arr.splice(idx, 0, moved);
    onChange(arr);
    setDragIdx(null); setDragOverIdx(null);
  };
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null); };

  return (
    <div className="space-y-3">
      {images.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {images.map((img, i) => (
            <div key={i} draggable onDragStart={(e) => handleDragStart(e, i)} onDragOver={(e) => handleDragOver(e, i)}
              onDrop={(e) => handleDrop(e, i)} onDragEnd={handleDragEnd}
              className={`relative group border rounded-lg overflow-hidden bg-[#fafafa] aspect-square cursor-grab active:cursor-grabbing transition-all
                ${dragOverIdx === i ? "ring-2 ring-[#0071e3] scale-105 border-[#0071e3]" : "border-[#d2d2d7]"}
                ${dragIdx === i ? "opacity-30" : ""}`}>
              <img src={img} alt="" className="w-full h-full object-contain" />
              <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1">
                <button onClick={() => moveImage(i, -1)} className="w-6 h-6 bg-white rounded text-xs font-bold" title="위로">↑</button>
                <button onClick={() => moveImage(i, 1)} className="w-6 h-6 bg-white rounded text-xs font-bold" title="아래로">↓</button>
                <button onClick={() => setEditingIdx(i)} className="w-6 h-6 bg-[#0071e3] text-white rounded text-xs" title="편집">✏️</button>
                <button onClick={() => removeImage(i)} className="w-6 h-6 bg-red-500 text-white rounded text-xs font-bold" title="삭제">×</button>
              </div>
              <span className="absolute top-1 left-1 bg-black/60 text-white text-[10px] px-1.5 py-0.5 rounded">{i + 1}</span>
            </div>
          ))}
        </div>
      )}
      <ImageUploadZone onUploaded={(url) => onChange([...images, url])} />
      {editingIdx !== null && images[editingIdx] && (
        <ImageEditorModal imageUrl={images[editingIdx]}
          onSave={(url) => { const arr = [...images]; arr[editingIdx] = url; onChange(arr); setEditingIdx(null); }}
          onClose={() => setEditingIdx(null)} />
      )}
    </div>
  );
}

/* ─── Markdown Editor with Toolbar ─── */
export function MarkdownEditor({ value, onChange, contentFormat, onFormatChange, uploadEndpoint }) {
  const editorRef = useRef(null);
  const [showPreview, setShowPreview] = useState(false);

  const insertText = useCallback((before, after = "", placeholder = "") => {
    const el = editorRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const selected = value.substring(start, end) || placeholder;
    const newText = value.substring(0, start) + before + selected + after + value.substring(end);
    onChange(newText);
    setTimeout(() => {
      el.focus();
      const pos = start + before.length + selected.length;
      el.setSelectionRange(start + before.length, pos);
    }, 10);
  }, [value, onChange]);

  const handleImageUpload = useCallback((url) => {
    if (contentFormat === "html") {
      insertText(`<img src="${url}" alt="상품 이미지" style="max-width:100%;" />\n`, "");
    } else {
      insertText(`![상품 이미지](${url})\n`, "");
    }
  }, [contentFormat, insertText]);

  const handleKeyDown = (e) => {
    if (e.key === "Tab") {
      e.preventDefault();
      insertText("  ", "");
    }
    if (e.ctrlKey || e.metaKey) {
      if (e.key === "b") { e.preventDefault(); wrapBold(); }
      if (e.key === "i") { e.preventDefault(); wrapItalic(); }
    }
  };

  const wrapBold = () => contentFormat === "html" ? insertText("<b>", "</b>", "굵은 텍스트") : insertText("**", "**", "굵은 텍스트");
  const wrapItalic = () => contentFormat === "html" ? insertText("<i>", "</i>", "기울임") : insertText("*", "*", "기울임");
  const wrapStrike = () => contentFormat === "html" ? insertText("<s>", "</s>", "취소선") : insertText("~~", "~~", "취소선");
  const insertH1 = () => contentFormat === "html" ? insertText("<h1>", "</h1>", "제목") : insertText("\n# ", "\n", "제목");
  const insertH2 = () => contentFormat === "html" ? insertText("<h2>", "</h2>", "소제목") : insertText("\n## ", "\n", "소제목");
  const insertH3 = () => contentFormat === "html" ? insertText("<h3>", "</h3>", "소제목") : insertText("\n### ", "\n", "소제목");
  const insertQuote = () => contentFormat === "html" ? insertText("<blockquote>", "</blockquote>", "인용문") : insertText("\n> ", "\n", "인용문");
  const insertCode = () => contentFormat === "html" ? insertText("<code>", "</code>", "코드") : insertText("`", "`", "코드");
  const insertCodeBlock = () => contentFormat === "html" ? insertText("<pre><code>\n", "\n</code></pre>", "코드 블록") : insertText("\n```\n", "\n```\n", "코드 블록");
  const insertUl = () => contentFormat === "html" ? insertText("<ul>\n<li>", "</li>\n</ul>", "항목") : insertText("\n- ", "\n", "항목");
  const insertOl = () => contentFormat === "html" ? insertText("<ol>\n<li>", "</li>\n</ol>", "항목") : insertText("\n1. ", "\n", "항목");
  const insertLink = () => contentFormat === "html" ? insertText('<a href="URL">', '</a>', '링크 텍스트') : insertText("[", "](URL)", "링크 텍스트");
  const insertHr = () => contentFormat === "html" ? insertText("\n<hr />\n", "") : insertText("\n---\n", "");
  const insertTable = () => {
    if (contentFormat === "html") {
      insertText(
        '<table>\n<thead><tr><th>항목</th><th>값</th></tr></thead>\n<tbody>\n<tr><td>',
        '</td><td>값</td></tr>\n</tbody></table>\n',
        "항목명"
      );
    } else {
      insertText("\n| 항목 | 값 |\n|------|------|\n| ", " | 값 |\n", "항목명");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const fd = new FormData();
      fd.append("image", file);
      api.post(uploadEndpoint || "/api/admin/upload-image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      }).then((res) => handleImageUpload(res.data.url))
        .catch((err) => alert("업로드 실패: " + (err.response?.data?.error || err.message)));
    }
  };

  return (
    <div className="border border-[#d2d2d7] rounded-xl overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center gap-0.5 px-2 py-1.5 bg-[#fafafa] border-b border-[#d2d2d7] flex-wrap">
        {/* Format switcher */}
        <select
          value={contentFormat}
          onChange={(e) => onFormatChange(e.target.value)}
          className="text-xs px-2 py-1 rounded-md border border-[#d2d2d7] mr-2 bg-white font-medium"
        >
          <option value="markdown">Markdown</option>
          <option value="html">HTML</option>
          <option value="text">Text</option>
        </select>

        <div className="w-px h-5 bg-[#d2d2d7] mx-1" />

        <TBtn onClick={wrapBold} title="굵게 (Ctrl+B)"><b>B</b></TBtn>
        <TBtn onClick={wrapItalic} title="기울임 (Ctrl+I)"><i>I</i></TBtn>
        <TBtn onClick={wrapStrike} title="취소선"><s>S</s></TBtn>

        <div className="w-px h-5 bg-[#d2d2d7] mx-1" />

        <TBtn onClick={insertH1} title="제목 1"><span className="text-xs font-bold">H1</span></TBtn>
        <TBtn onClick={insertH2} title="제목 2"><span className="text-xs font-bold">H2</span></TBtn>
        <TBtn onClick={insertH3} title="제목 3"><span className="text-xs font-bold">H3</span></TBtn>

        <div className="w-px h-5 bg-[#d2d2d7] mx-1" />

        <TBtn onClick={insertQuote} title="인용문">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" /></svg>
        </TBtn>
        <TBtn onClick={insertCode} title="인라인 코드">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" /></svg>
        </TBtn>
        <TBtn onClick={insertCodeBlock} title="코드 블록">
          <span className="text-[10px] font-mono">{"{}"}</span>
        </TBtn>

        <div className="w-px h-5 bg-[#d2d2d7] mx-1" />

        <TBtn onClick={insertUl} title="순서없는 목록">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zM3.75 12h.007v.008H3.75V12zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm-.375 5.25h.007v.008H3.75v-.008zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z" /></svg>
        </TBtn>
        <TBtn onClick={insertOl} title="순서있는 목록">
          <span className="text-[10px] font-bold">1.</span>
        </TBtn>
        <TBtn onClick={insertLink} title="링크">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m9.86-4.122a4.5 4.5 0 00-1.242-7.244l-4.5-4.5a4.5 4.5 0 00-6.364 6.364L4.28 8.77" /></svg>
        </TBtn>
        <TBtn onClick={insertTable} title="표">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h7.5c.621 0 1.125-.504 1.125-1.125m-9.75 0V5.625m0 12.75v-1.5c0-.621.504-1.125 1.125-1.125m18.375 2.625V5.625m0 12.75c0 .621-.504 1.125-1.125 1.125m1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125m0 3.75h-7.5A1.125 1.125 0 0112 18.375m9.75-12.75c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125m19.5 0v1.5c0 .621-.504 1.125-1.125 1.125M2.25 5.625v1.5c0 .621.504 1.125 1.125 1.125m0 0h17.25m-17.25 0h7.5c.621 0 1.125.504 1.125 1.125M3.375 8.25c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125m17.25-3.75h-7.5c-.621 0-1.125.504-1.125 1.125m8.625-1.125c.621 0 1.125.504 1.125 1.125v1.5c0 .621-.504 1.125-1.125 1.125m-17.25 0h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125M12 10.875v-1.5m0 1.5c0 .621-.504 1.125-1.125 1.125M12 10.875c0 .621.504 1.125 1.125 1.125m-2.25 0c.621 0 1.125.504 1.125 1.125M12 12h7.5m-7.5 0c-.621 0-1.125.504-1.125 1.125M3.375 15h7.5m0 0c.621 0 1.125-.504 1.125-1.125M12 15v-1.5" /></svg>
        </TBtn>
        <TBtn onClick={insertHr} title="구분선">—</TBtn>

        <div className="w-px h-5 bg-[#d2d2d7] mx-1" />

        <ImageUploadZone compact onUploaded={handleImageUpload} />

        <div className="flex-1" />

        {/* Preview toggle */}
        <button
          onClick={() => setShowPreview(!showPreview)}
          className={`text-xs px-3 py-1 rounded-md transition font-medium ${
            showPreview ? "bg-[#0071e3] text-white" : "text-[#6e6e73] hover:bg-[#f5f5f7]"
          }`}
        >
          {showPreview ? "편집" : "미리보기"}
        </button>
      </div>

      {/* Editor / Preview */}
      {showPreview ? (
        <div className="p-6 min-h-[400px] max-h-[600px] overflow-y-auto bg-white">
          {contentFormat === "markdown" ? (
            <div className="prose max-w-none">
              <ReactMarkdown>{value || ""}</ReactMarkdown>
            </div>
          ) : contentFormat === "html" ? (
            <div className="prose max-w-none product-detail-html" dangerouslySetInnerHTML={{ __html: value || "" }} />
          ) : (
            <pre className="whitespace-pre-wrap text-sm text-[#1d1d1f] font-sans">{value || ""}</pre>
          )}
        </div>
      ) : (
        <textarea
          ref={editorRef}
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
          className="w-full min-h-[400px] max-h-[600px] p-4 resize-y font-mono text-sm text-[#1d1d1f] outline-none bg-white"
          placeholder={contentFormat === "markdown"
            ? "# 상품 상세 설명\n\n마크다운으로 작성하세요...\n\n## 주요 기능\n- 기능 1\n- 기능 2"
            : contentFormat === "html"
            ? "<h2>상품 상세 설명</h2>\n<p>HTML로 작성하세요...</p>"
            : "상품 상세 설명을 입력하세요..."}
        />
      )}
    </div>
  );
}

/* ─── Custom Specs Editor (자유 항목 추가) ─── */
function CustomSpecsEditor({ specsJson, onChange }) {
  // specsJson is a dict-like object: { "항목명": "값", ... }
  const specs = specsJson && typeof specsJson === "object" && !Array.isArray(specsJson) ? specsJson : {};
  const entries = Object.entries(specs);

  const addSpec = () => {
    onChange({ ...specs, "": "" });
  };

  const updateKey = (oldKey, newKey) => {
    const newSpecs = {};
    for (const [k, v] of Object.entries(specs)) {
      newSpecs[k === oldKey ? newKey : k] = v;
    }
    onChange(newSpecs);
  };

  const updateValue = (key, newVal) => {
    onChange({ ...specs, [key]: newVal });
  };

  const removeSpec = (key) => {
    const { [key]: _, ...rest } = specs;
    onChange(rest);
  };

  return (
    <div className="space-y-2">
      {entries.map(([key, value], idx) => (
        <div key={idx} className="flex gap-2 items-center">
          <input
            value={key}
            onChange={(e) => updateKey(key, e.target.value)}
            className="flex-[2] px-3 py-2 rounded-lg border border-[#d2d2d7] text-sm focus:border-[#0071e3] focus:ring-1 focus:ring-[#0071e3] outline-none"
            placeholder="항목명 (예: 해상도)"
          />
          <input
            value={value}
            onChange={(e) => updateValue(key, e.target.value)}
            className="flex-[3] px-3 py-2 rounded-lg border border-[#d2d2d7] text-sm focus:border-[#0071e3] focus:ring-1 focus:ring-[#0071e3] outline-none"
            placeholder="값 (예: 1200×1200dpi)"
          />
          <button
            type="button"
            onClick={() => removeSpec(key)}
            className="w-8 h-8 rounded-lg text-red-400 hover:bg-red-50 hover:text-red-600 transition flex items-center justify-center shrink-0"
            title="삭제"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={addSpec}
        className="flex items-center gap-1.5 text-sm text-[#0071e3] hover:text-[#0077ed] transition font-medium"
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
        </svg>
        항목 추가
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════
   ProductEditor (메인 컴포넌트)
   ═══════════════════════════════════════════════════════════ */
const inp = "w-full px-3 py-2 rounded-lg border border-[#d2d2d7] text-sm focus:border-[#0071e3] focus:ring-1 focus:ring-[#0071e3] outline-none";
const label = "block text-xs font-medium text-[#6e6e73] mb-1";

export default function ProductEditor({ form, setForm, onSave, onCancel, type, isNew, saving }) {
  const [activeTab, setActiveTab] = useState("basic");

  const F = (key) => ({
    value: form[key] ?? "",
    onChange: (e) => setForm({ ...form, [key]: e.target.value }),
  });

  const Fn = (key) => ({
    value: form[key] ?? 0,
    onChange: (e) => setForm({ ...form, [key]: parseInt(e.target.value) || 0 }),
  });

  const Fb = (key) => ({
    checked: !!form[key],
    onChange: (e) => setForm({ ...form, [key]: e.target.checked }),
  });

  // Parse detail_images
  let detailImages = [];
  try {
    if (Array.isArray(form.detail_images)) detailImages = form.detail_images;
    else if (typeof form.detail_images === "string") detailImages = JSON.parse(form.detail_images);
  } catch { /* ignore */ }

  const tabs = [
    { key: "basic", label: "기본 정보", icon: "📋" },
    { key: "specs", label: "가격/스펙", icon: "⚙️" },
    { key: "editor", label: "상세 편집기", icon: "📝" },
    { key: "images", label: "이미지 관리", icon: "🖼️" },
    { key: "preview", label: "미리보기", icon: "👁️" },
  ];

  return (
    <div className="bg-white rounded-2xl border border-[#d2d2d7] overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-[#d2d2d7] bg-[#fafafa] flex items-center justify-between">
        <h2 className="text-lg font-bold text-[#1d1d1f]">
          {isNew ? "새 상품 등록" : "상품 편집"}
          {form.name && <span className="font-normal text-[#86868b] ml-2">— {form.name}</span>}
        </h2>
        {onCancel && (
          <button onClick={onCancel} className="w-8 h-8 flex items-center justify-center rounded-full text-[#86868b] hover:bg-[#e8e8ed] hover:text-[#1d1d1f] transition" title="닫기">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[#d2d2d7] bg-[#fafafa]">
        {tabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`flex items-center gap-1.5 px-5 py-3 text-sm font-medium transition border-b-2 -mb-px ${
              activeTab === t.key
                ? "border-[#0071e3] text-[#0071e3]"
                : "border-transparent text-[#6e6e73] hover:text-[#1d1d1f]"
            }`}
          >
            <span>{t.icon}</span>
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="p-6">
        {/* ─── 기본 정보 ─── */}
        {activeTab === "basic" && (
          <div className="space-y-4 max-w-2xl">
            <div>
              <label className={label}>상품명 *</label>
              <input {...F("name")} className={inp} placeholder="상품명을 입력하세요" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>카테고리</label>
                <input {...F("category")} className={inp} placeholder="컬러복합기, 흑백복합기, 토너 등" />
              </div>
              <div>
                <label className={label}>브랜드</label>
                <input {...F("brand")} className={inp} placeholder="삼성, 제록스, HP 등" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={label}>모델명</label>
                <input {...F("model")} className={inp} placeholder="SL-X1200" />
              </div>
              <div>
                <label className={label}>판매 유형</label>
                <select value={form.sale_type || (type === "sktec" ? "sale" : "rental")}
                  onChange={(e) => setForm({ ...form, sale_type: e.target.value })} className={inp}>
                  <option value="sale">판매 (장바구니)</option>
                  <option value="rental">렌탈 (상담문의)</option>
                  <option value="both">판매 + 렌탈</option>
                </select>
              </div>
            </div>
            <div>
              <label className={label}>서비스 지역</label>
              <input {...F("district")} className={inp} placeholder="전국, 서울, 강남구 등" />
            </div>
            <div>
              <label className={label}>상품 외부 URL</label>
              <input {...F("product_url")} className={inp} placeholder="https://..." />
            </div>
            <div>
              <label className={label}>상태</label>
              <select value={form.is_active === false ? "0" : "1"}
                onChange={(e) => setForm({ ...form, is_active: e.target.value === "1" })} className={inp}>
                <option value="1">활성</option>
                <option value="0">비활성</option>
              </select>
            </div>
          </div>
        )}

        {/* ─── 가격/스펙 ─── */}
        {activeTab === "specs" && (
          <div className="space-y-6 max-w-2xl">
            {/* Price section */}
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#0071e3] rounded-full" />
                가격 정보
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={label}>판매가 (원)</label>
                  <input type="number" {...Fn("price")} className={inp} />
                </div>
                {type === "ikoffices" && (
                  <>
                    <div>
                      <label className={label}>월 렌탈료 (원)</label>
                      <input type="number" {...Fn("rental_price")} className={inp} />
                    </div>
                    <div>
                      <label className={label}>계약기간</label>
                      <input {...F("contract_period")} className={inp} placeholder="5년" />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Spec section */}
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#34c759] rounded-full" />
                제품 사양
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={label}>흑백 속도 (ppm)</label>
                  <input type="number" {...Fn("bw_speed")} className={inp} placeholder="0" />
                </div>
                <div>
                  <label className={label}>컬러 속도 (ppm)</label>
                  <input type="number" {...Fn("color_speed")} className={inp} placeholder="0" />
                </div>
                <div>
                  <label className={label}>용지 크기</label>
                  <input {...F("paper_size")} className={inp} placeholder="A3, A4 등" />
                </div>
                <div>
                  <label className={label}>월 권장 출력량</label>
                  <input {...F("monthly_volume")} className={inp} placeholder="10,000매" />
                </div>
              </div>
              <div className="flex gap-6 mt-4">
                <label className="flex items-center gap-2 text-sm text-[#1d1d1f] cursor-pointer">
                  <input type="checkbox" {...Fb("has_fax")} className="w-4 h-4 rounded border-[#d2d2d7] text-[#0071e3] focus:ring-[#0071e3]" />
                  팩스 기능
                </label>
                <label className="flex items-center gap-2 text-sm text-[#1d1d1f] cursor-pointer">
                  <input type="checkbox" {...Fb("duplex")} className="w-4 h-4 rounded border-[#d2d2d7] text-[#0071e3] focus:ring-[#0071e3]" />
                  양면 인쇄
                </label>
              </div>
            </div>

            {/* Custom specs */}
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-2 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#ff9500] rounded-full" />
                추가 사양 (자유 입력)
              </h3>
              <p className="text-xs text-[#86868b] mb-3">상세페이지에 표시될 추가 스펙을 자유롭게 입력하세요.</p>
              <CustomSpecsEditor
                specsJson={(() => {
                  let s = form.specs_json;
                  if (!s) return {};
                  if (typeof s === "string") { try { return JSON.parse(s); } catch { return {}; } }
                  return s;
                })()}
                onChange={(newSpecs) => setForm({ ...form, specs_json: newSpecs })}
              />
            </div>
          </div>
        )}

        {/* ─── 상세 편집기 (마크다운/HTML/텍스트) ─── */}
        {activeTab === "editor" && (
          <div>
            <MarkdownEditor
              value={form.description_html || ""}
              onChange={(v) => setForm({ ...form, description_html: v })}
              contentFormat={form.content_format || "html"}
              onFormatChange={(f) => setForm({ ...form, content_format: f })}
            />
          </div>
        )}

        {/* ─── 이미지 관리 ─── */}
        {activeTab === "images" && (
          <div className="space-y-6 max-w-2xl">
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#0071e3] rounded-full" />
                대표 이미지
              </h3>
              <MainImageUploader
                imageUrl={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
              />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-[#1d1d1f] mb-3 flex items-center gap-2">
                <span className="w-1 h-4 bg-[#34c759] rounded-full" />
                상세 이미지 ({detailImages.length}개)
              </h3>
              <DetailImagesManager
                images={detailImages}
                onChange={(imgs) => setForm({ ...form, detail_images: imgs })}
              />
            </div>
          </div>
        )}

        {/* ─── 미리보기 (Product Preview) ─── */}
        {activeTab === "preview" && (
          <div className="bg-white">
            <div className="mb-4 flex items-center gap-2 text-[13px] text-[#86868b]">
              <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              실시간 미리보기 — 실제 상품 페이지와 동일한 레이아웃
            </div>

            {/* Preview Container */}
            <div className="border border-[#d2d2d7] rounded-2xl overflow-hidden bg-white">
              {/* Product Hero */}
              <div className="grid md:grid-cols-2 gap-8 p-6">
                {/* Image */}
                <div>
                  <div className="bg-[#fafafa] rounded-2xl aspect-square flex items-center justify-center overflow-hidden">
                    {form.image_url ? (
                      <img src={form.image_url} alt={form.name || ""} className="w-[75%] h-[75%] object-contain" onError={e => { e.target.style.display = "none"; }} />
                    ) : (
                      <div className="text-[#d2d2d7] text-center">
                        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" strokeWidth={0.8} viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0 0 22.5 18.75V5.25A2.25 2.25 0 0 0 20.25 3H3.75A2.25 2.25 0 0 0 1.5 5.25v13.5A2.25 2.25 0 0 0 3.75 21Z" />
                        </svg>
                        <p className="text-xs mt-2">대표 이미지 없음</p>
                      </div>
                    )}
                  </div>
                  {/* Thumbnail strip */}
                  {detailImages.length > 0 && (
                    <div className="flex gap-1.5 mt-3 overflow-x-auto scrollbar-none">
                      {form.image_url && (
                        <div className="shrink-0 w-[48px] h-[48px] rounded-lg border-2 border-[#0071e3] overflow-hidden bg-[#fafafa]">
                          <img src={form.image_url} alt="" className="w-full h-full object-contain" />
                        </div>
                      )}
                      {detailImages.slice(0, 6).map((img, i) => (
                        <div key={i} className="shrink-0 w-[48px] h-[48px] rounded-lg border border-[#d2d2d7] overflow-hidden bg-[#fafafa]">
                          <img src={img} alt="" className="w-full h-full object-contain" />
                        </div>
                      ))}
                      {detailImages.length > 6 && (
                        <div className="shrink-0 w-[48px] h-[48px] rounded-lg border border-[#d2d2d7] bg-[#fafafa] flex items-center justify-center text-[10px] text-[#86868b]">
                          +{detailImages.length - 6}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Product Info */}
                <div>
                  {/* Badges */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                      type === "sktec" ? "bg-[#0071e3]/10 text-[#0071e3]" : "bg-[#34c759]/10 text-[#34c759]"
                    }`}>
                      {type === "sktec" ? "자사몰" : "렌탈"}
                    </span>
                    {form.sale_type && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
                        {form.sale_type === "sale" ? "판매" : form.sale_type === "rental" ? "렌탈" : "판매+렌탈"}
                      </span>
                    )}
                    {form.district && (
                      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-600">📍 {form.district}</span>
                    )}
                  </div>

                  <h1 className="text-[24px] font-bold text-[#1d1d1f] leading-tight mb-1">{form.name || "상품명을 입력하세요"}</h1>
                  {(form.brand || form.model) && (
                    <p className="text-[14px] text-[#6e6e73]">{form.brand}{form.brand && form.model && " · "}{form.model}</p>
                  )}

                  {/* Price */}
                  <div className="mt-4 mb-4">
                    {(form.rental_price || 0) > 0 && (
                      <div className="mb-1">
                        <p className="text-[11px] text-[#6e6e73]">월 렌탈료</p>
                        <p className="text-[28px] font-bold text-[#1d1d1f]">{Number(form.rental_price).toLocaleString()}<span className="text-[14px] font-normal text-[#6e6e73]">원/월</span></p>
                      </div>
                    )}
                    {(form.price || 0) > 0 && (
                      <div>
                        <p className="text-[11px] text-[#6e6e73]">{(form.rental_price || 0) > 0 ? "판매가" : "가격"}</p>
                        <p className={`font-bold ${(form.rental_price || 0) > 0 ? "text-[18px] text-[#86868b]" : "text-[28px] text-[#1d1d1f]"}`}>
                          {Number(form.price).toLocaleString()}<span className="text-[14px] font-normal text-[#6e6e73]">원</span>
                        </p>
                      </div>
                    )}
                    {!(form.price || 0) && !(form.rental_price || 0) && (
                      <p className="text-[18px] font-semibold text-[#86868b]">가격문의</p>
                    )}
                  </div>

                  {/* Specs card preview */}
                  <div className="border border-[#d2d2d7] rounded-xl overflow-hidden text-[12px]">
                    <div className="px-4 py-2.5 border-b border-[#d2d2d7]/60 bg-[#fafafa]">
                      <span className="font-semibold text-[#1d1d1f]">{form.name || "제품 사양"}</span>
                    </div>
                    <div className="divide-y divide-[#d2d2d7]/40">
                      {form.brand && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">브랜드</span><span className="text-[#1d1d1f] font-medium">{form.brand}</span></div>}
                      {form.model && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">모델명</span><span className="text-[#1d1d1f] font-medium">{form.model}</span></div>}
                      {form.category && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">카테고리</span><span className="text-[#1d1d1f] font-medium">{form.category}</span></div>}
                      {(form.bw_speed || 0) > 0 && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">흑백 속도</span><span className="text-[#1d1d1f] font-medium">{form.bw_speed}ppm</span></div>}
                      {(form.color_speed || 0) > 0 && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">컬러 속도</span><span className="text-[#1d1d1f] font-medium">{form.color_speed}ppm</span></div>}
                      {form.paper_size && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">용지 크기</span><span className="text-[#1d1d1f] font-medium">{form.paper_size}</span></div>}
                      {form.has_fax && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">팩스</span><span className="text-[#1d1d1f] font-medium">지원</span></div>}
                      {form.duplex && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">양면 인쇄</span><span className="text-[#1d1d1f] font-medium">지원</span></div>}
                      {form.contract_period && <div className="flex justify-between px-4 py-2"><span className="text-[#86868b]">계약기간</span><span className="text-[#1d1d1f] font-medium">{form.contract_period}</span></div>}
                      {/* Custom specs from specs_json */}
                      {(() => {
                        let s = form.specs_json;
                        if (!s) return null;
                        if (typeof s === "string") { try { s = JSON.parse(s); } catch { return null; } }
                        return Object.entries(s).filter(([k, v]) => k && v).map(([k, v], i) => (
                          <div key={`cs-${i}`} className="flex justify-between px-4 py-2">
                            <span className="text-[#86868b]">{k}</span>
                            <span className="text-[#1d1d1f] font-medium">{v}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>

                  {/* CTA buttons preview */}
                  <div className="mt-4 space-y-2">
                    <div className="flex gap-2">
                      <div className="flex-1 bg-[#0071e3] text-white text-center rounded-xl py-2.5 text-[13px] font-medium">장바구니 담기</div>
                      <div className="w-10 bg-[#f5f5f7] rounded-xl flex items-center justify-center text-[#86868b]">♡</div>
                    </div>
                    <div className="bg-[#1d1d1f] text-white text-center rounded-xl py-2.5 text-[13px] font-medium">AI 상담하기</div>
                  </div>
                </div>
              </div>

              {/* Description content preview */}
              {form.description_html && (
                <div className="border-t border-[#d2d2d7]/40 p-6">
                  <h3 className="text-[20px] font-bold text-[#1d1d1f] mb-4">상품 상세정보</h3>
                  {(form.content_format || "html") === "markdown" ? (
                    <div className="prose max-w-none text-sm">
                      <ReactMarkdown>{form.description_html}</ReactMarkdown>
                    </div>
                  ) : (form.content_format || "html") === "text" ? (
                    <pre className="whitespace-pre-wrap text-sm text-[#1d1d1f] font-sans leading-relaxed">{form.description_html}</pre>
                  ) : (
                    <div className="prose max-w-none text-sm product-detail-html" dangerouslySetInnerHTML={{ __html: form.description_html }} />
                  )}
                </div>
              )}

              {/* Detail images preview */}
              {detailImages.length > 0 && (
                <div className="border-t border-[#d2d2d7]/40 p-6">
                  <h3 className="text-[20px] font-bold text-[#1d1d1f] mb-4">상품 이미지</h3>
                  <div className="grid grid-cols-2 gap-3">
                    {detailImages.map((img, i) => (
                      <img key={i} src={img} alt="" className="w-full rounded-lg" loading="lazy" />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-[#d2d2d7] bg-[#fafafa] flex justify-between items-center">
        <div className="text-xs text-[#86868b]">
          {form.content_format === "markdown" && "📝 Markdown"}
          {form.content_format === "html" && "📄 HTML"}
          {form.content_format === "text" && "📃 Text"}
          {!form.content_format && "📄 HTML"}
          {form.description_html ? ` · ${form.description_html.length}자` : ""}
          {detailImages.length > 0 && ` · 이미지 ${detailImages.length}장`}
        </div>
        <div className="flex gap-2">
          {onCancel && (
            <button onClick={onCancel}
              className="px-5 py-2.5 rounded-xl text-sm font-medium text-[#6e6e73] hover:bg-[#f5f5f7] transition">
              취소
            </button>
          )}
          <button onClick={onSave} disabled={saving}
            className="px-6 py-2.5 rounded-xl text-sm font-medium bg-[#0071e3] text-white hover:bg-[#0077ed] transition disabled:opacity-50 disabled:cursor-not-allowed">
            {saving ? "저장 중..." : isNew ? "등록" : "저장"}
          </button>
        </div>
      </div>
    </div>
  );
}
